import { Notes, Continue, SlideLayout } from "./Directives";
import ChangeDeck from "./ChangeDeck";
import Splash from "./layout/Splash";
import TestPattern from "./TestPattern";
import Transclude from "./Transclude";

export const title = "TetraMem";

<SlideLayout use={TestPattern} />

---

<SlideLayout use={Splash} />

# TetraMem

---

# Arenas

- Primarily used for performance
  - Amortizes memory allocations and deallocations
  - Benefits from cache locality

- No free lunch
  - Memory usage grows

<Notes />

- Best fit with plain-old-data types, otherwise still have O(N) `Drop` calls
- Caution for false sharing if used in multithreaded mutation

---

# Rust-specific benefits

- Allocated values all share the same lifetime
- Allocation method may not need `&mut`

<Notes />

---

# Cycles

import cycle from "rust:tetramem/cycle";

<Transclude src={cycle} />

---

# Cycles

import cycleStderr from "stderr:tetramem/cycle";

<Transclude src={cycle} focusOn="17+7" />

<Transclude src={cycleStderr} lang="compiler-error" focusOn="1+11" />

<Notes />

- Requires mutable reference while already borrowed

---

# Add interior mutability

import cycleRev1 from "rust:tetramem/cycle?rev=1";

<Transclude
  src={cycleRev1}
  emphasize="1,5[19+5],5[45+1],6[20+5],6[46+1],13[31+7],14[32+7]20[4+8],21[4+9],23[23+5],23[40+1],24[23+5],24[39+1]"
/>

<Notes />

- This works, but the number of houses is fixed at compile time as
  each is a separate variable.

---

# Make it dynamic

import cycleRev2 from "rust:tetramem/cycle?rev=2";

<Transclude
  src={cycleRev2}
  focusOn="19+8"
  emphasize="2,3[15+16],3[44+1],4[16+16],4[45+1]"
/>

<Notes />

- `Vec::push_mut` is relatively new

<Continue />

import cycleStderrRev2 from "stderr:tetramem/cycle?rev=2";

<Transclude src={cycleStderrRev2} lang="compiler-error" focusOn="1+10" />

<Notes />

- Can't add second item because pushing (and reallocating) might
  invalidate references to the first item

---

# Get all references at once

import cycleRev3 from "rust:tetramem/cycle?rev=3";

<Transclude src={cycleRev3} focusOn="19+12" emphasize="3,4[11+4],5,6[11+4],8" />

<Notes />

- Let intermediate references be invalidated; this compiles

---

# Get all references at once

import cycleRev4 from "rust:tetramem/cycle?rev=4";

<Transclude src={cycleRev4} focusOn="19+14" emphasize="13" />

<Continue />

import cycleRev4Stderr from "stderr:tetramem/cycle?rev=4";

<Transclude src={cycleRev4Stderr} lang="compiler-error" focusOn="1+11" />

<Notes />

- Can't add any more; we are back where we started
- Limited as the number of indices is fixed at compile time

---

# Arenas to the rescue

import cycleRev5 from "rust:tetramem/cycle?rev=5";

<Transclude
  src={cycleRev5}
  focusOn="3+2,21+10"
  emphasize="1,4,5[22+5],6[23+5]"
/>

<Notes />

- `Arena::alloc` takes in `&self` as there's no way it will invalidate previous references

---

# Indices to the rescue

import cycleRev6 from "rust:tetramem/cycle?rev=6";

<Transclude
  src={cycleRev6}
  focusOn="3+6,19+12"
  emphasize="3[31+5],4[32+5],14[4+12],15[4+13]"
/>

<Notes />

- This is trivially available and memory safe
- Introduces fallibility, here via the panic during indexing
- Lookups may be slower due to indirection
- Possibility of index confusion via `usize`
  - Encourage newtyping
- Index can be invalidated via typical `Vec` methods

---

# "Arena" is a broad term

- [https://donsz.nl/blog/arenas](https://donsz.nl/blog/arenas)
- What is returned from allocation?
  - Reference vs index
- Can elements be freed?
  - Is accidental reuse of indices prevented?

<Notes />

- Reuse of indices often called the ABA problem

---

# Topic smörgåsbord
## Dynamic dispatch

- Explicit enums (closed set) vs dyn trait (open set)
- [`enum_dispatch`](https://docs.rs/enum_dispatch/)
- [`dyn Any`](https://doc.rust-lang.org/stable/std/any/trait.Any.html)

---

# Topic smörgåsbord
## Interesting types

- [`PhantomData`](https://doc.rust-lang.org/stable/std/marker/struct.PhantomData.html)
- [`UnsafeCell`](https://doc.rust-lang.org/stable/std/cell/struct.UnsafeCell.html)
- [`Pin`](https://doc.rust-lang.org/stable/std/pin/struct.Pin.html)

---

# Topic smörgåsbord
## Performance

- [`#[inline]`](https://doc.rust-lang.org/beta/reference/attributes/codegen.html#r-attributes.codegen.inline)
- [`cold_path`](https://doc.rust-lang.org/stable/std/hint/fn.cold_path.html)
- [`assert_unchecked`](https://doc.rust-lang.org/stable/std/hint/fn.assert_unchecked.html)

---

# Topic smörgåsbord
## Building

- Crates are the unit of compilation
- [Cargo workspaces](https://doc.rust-lang.org/cargo/reference/workspaces.html)
- [Build timing](https://doc.rust-lang.org/cargo/reference/timings.html)
- [Link-time optimization (LTO)](https://doc.rust-lang.org/cargo/reference/profiles.html#lto)

---

# Topic smörgåsbord
## Third party tools

- [`cargo audit`](https://crates.io/crates/cargo-audit)
- [`cargo crev`](https://crates.io/crates/cargo-crev)
- [`cargo geiger`](https://crates.io/crates/cargo-geiger)

---

<SlideLayout use={Splash} />

# <ChangeDeck deck="overview">Return</ChangeDeck>
